import { useEffect, useState } from 'react';
import { Pencil, Lock } from 'lucide-react';
import Modal from '../common/Modal';
import FileTypeIcon from './FileTypeIcon';
import documentService from '../../services/documentService';

/**
 * Edit metadata (the DOCUMENT_EDIT permission): description, tags and the confidential
 * flag. Renaming has its own dialog. A confidential document cannot be shared externally
 * and its downloads are audited with IP capture.
 */
export default function EditDetailsDialog({ open, onClose, document: doc, onSaved }) {
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && doc) {
      setDescription(doc.description || '');
      setTags((doc.tags || []).join(', '));
      setConfidential(Boolean(doc.isConfidential));
      setError(null);
    }
  }, [open, doc]);

  if (!doc) return null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const tagList = [...new Set(tags.split(/[,\n]/).map((t) => t.trim().toLowerCase()).filter(Boolean))].slice(0, 20);
      await documentService.update(doc.id, { description: description.trim(), tags: tagList, isConfidential: confidential });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'The details could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} width={480}>
      <div className="stack" style={{ gap: 16, paddingTop: 22 }}>
        <div className="row" style={{ gap: 12 }}>
          <FileTypeIcon name={doc.name} size="lg" />
          <div style={{ minWidth: 0 }}>
            <div className="row" style={{ gap: 8, fontWeight: 700, fontSize: 16 }}>
              <Pencil size={16} /> Edit details
            </div>
            <div className="muted truncate" style={{ fontSize: 12.5 }}>
              {doc.name}
            </div>
          </div>
        </div>

        <div>
          <label className="lbl" htmlFor="edit-desc">
            Description
          </label>
          <textarea id="edit-desc" className="in" placeholder="What is this document, and who is it for?" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} style={{ height: 88 }} />
        </div>

        <div>
          <label className="lbl" htmlFor="edit-tags">
            Tags
          </label>
          <input id="edit-tags" className="in" placeholder="agreement, landmark, renewal" value={tags} onChange={(e) => setTags(e.target.value)} />
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Comma-separated. Tags are searchable and appear as filters.
          </div>
        </div>

        <label className="rad" style={{ alignItems: 'center' }}>
          <input type="checkbox" checked={confidential} onChange={(e) => setConfidential(e.target.checked)} />
          <span className="dot" style={{ borderRadius: 4 }} />
          <span>
            <div className="row" style={{ gap: 6, fontWeight: 600, fontSize: 13.5 }}>
              <Lock size={13} /> Confidential
            </div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Blocks external links and records the IP address on every download.
            </div>
          </span>
        </label>

        {error && <div className="banner banner--danger">{error}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-g" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-p" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save details'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
